import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import { resolveFileContentType } from "../utils/s3Upload";

const API_URL = process.env.REACT_APP_API_URL;
const MAX_SUPPLIER_ATTACHMENTS = 5;
const SUPPLIER_ALLOWED_EXTENSIONS = ["jpeg", "jpg", "png", "pdf", "xlsx", "ppt"];

function parseApiResponse(raw) {
  let d = raw;
  if (typeof d === "string") {
    try {
      d = JSON.parse(d);
    } catch {
      return raw;
    }
  }
  if (d?.body != null) {
    if (typeof d.body === "string") {
      try {
        return JSON.parse(d.body);
      } catch {
        return d.body;
      }
    }
    return d.body;
  }
  return d;
}

function parseJsonIfString(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeApiPayload(raw) {
  let obj = raw;
  if (!obj || typeof obj !== "object") return raw;
  obj = { ...obj };
  if (typeof obj.data === "string") {
    obj.data = parseJsonIfString(obj.data);
  }
  return obj;
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== "object") return false;
  const s = responseData.success;
  return s === true || s === "true" || s === 1 || s === "1";
}

function pickRfqFromResponse(responseData) {
  let dataBlock = responseData?.data != null ? responseData.data : responseData;
  if (dataBlock == null) return null;
  if (typeof dataBlock === "string") {
    dataBlock = parseJsonIfString(dataBlock);
  }
  if (!dataBlock || typeof dataBlock !== "object") return null;

  if (dataBlock.rfq && typeof dataBlock.rfq === "object") {
    return dataBlock.rfq;
  }

  if (
    !Array.isArray(dataBlock) &&
    (dataBlock.rfqNumber != null ||
      dataBlock.rfqNo != null ||
      dataBlock.materials != null ||
      dataBlock.terms != null)
  ) {
    return dataBlock;
  }

  return null;
}

function formatDate(value) {
  if (!value) return "-";
  const str = String(value).trim();
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str.replace(/-/g, "/");
  const dateTimeMatch = str.match(/^(\d{2})-(\d{2})-(\d{4})[ T]/);
  if (dateTimeMatch) {
    return `${dateTimeMatch[1]}/${dateTimeMatch[2]}/${dateTimeMatch[3]}`;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return str || "-";
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value || "";
  return `${get("day")}/${get("month")}/${get("year")}`;
}

function formatDueDateTime(value) {
  if (!value) return "-";
  const str = String(value).trim();
  if (/^\d{2}-\d{2}-\d{4}[ T]\d{2}:\d{2}/.test(str)) {
    return str.replace("T", " ");
  }
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str.replace(/-/g, "/");
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return str || "-";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function formatDateTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);

  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(d);

  const get = (type) => parts.find((p) => p.type === type)?.value || "";
  const dayPeriod = get("dayPeriod").toUpperCase();
  return `${get("day")}/${get("month")}/${get("year")}, ${get("hour")}:${get("minute")} ${dayPeriod}`;
}

function parseRfqDueDate(value) {
  if (!value) return null;
  const str = String(value).trim();

  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
    const [dd, mm, yyyy] = str.split("-");
    return new Date(Number(yyyy), Number(mm) - 1, Number(dd), 23, 59, 59, 999);
  }

  if (/^\d{2}-\d{2}-\d{4}[ T]\d{2}:\d{2}/.test(str)) {
    const [datePart, timePart] = str.split(/[ T]/);
    const [dd, mm, yyyy] = datePart.split("-");
    const [hh, min] = timePart.split(":");
    return new Date(
      Number(yyyy),
      Number(mm) - 1,
      Number(dd),
      Number(hh),
      Number(min),
      59,
      999,
    );
  }

  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function isRfqDueDatePassed(dueDate) {
  const due = parseRfqDueDate(dueDate);
  if (!due) return false;
  return Date.now() > due.getTime();
}

function capitalizeStatus(value) {
  if (!value) return "-";
  const s = String(value);
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function isDecimalInput(value) {
  if (value === "" || value == null) return true;
  const normalized = String(value).trim().replace(/,/g, "");
  return /^\d*\.?\d*$/.test(normalized);
}

function normalizeDecimalInput(value) {
  if (value == null) return "";
  return String(value).trim().replace(/,/g, "").replace(/\s/g, "");
}

function parseDecimalValue(value) {
  const normalized = normalizeDecimalInput(value);
  if (normalized === "" || normalized === ".") return NaN;
  return parseFloat(normalized);
}

function formatAmount(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "0.00";
  return num.toFixed(2);
}

function calculateMaterialPricing(material, priceRow) {
  const quantity = parseFloat(material?.quantity) || 0;
  const basicPrice = parseDecimalValue(priceRow?.basicPrice);
  const gstPercent = parseDecimalValue(priceRow?.gstPercent);
  const lineBasic = basicPrice * quantity;
  const gstValue = (lineBasic * gstPercent) / 100;
  const totalValue = lineBasic + gstValue;
  return { quantity, basicPrice, gstPercent, lineBasic, gstValue, totalValue };
}

function isQuoteSubmitted(rfq) {
  if (!rfq) return false;
  if (rfq.quoteSubmitted === true || rfq.quoteSubmitted === "true") return true;
  if (rfq.quoteSubmittedAt) return true;
  const status = String(rfq.quoteStatus || "")
    .trim()
    .toLowerCase();
  return status === "submitted";
}

function normalizeQuoteResponse(value) {
  const str = String(value ?? "").trim();
  return str === "-" ? "" : str;
}

function getMaterialPrId(item) {
  return String(item?.pr_id || item?.prId || "").trim();
}

/** Leave empty for unset/zero API defaults so inputs don't show "0". */
function toEditableNumberInput(value) {
  if (value == null || value === "") return "";
  const str = String(value).trim();
  if (str === "" || str === "-" || str === ".") return "";
  const num = Number(str.replace(/,/g, ""));
  if (!Number.isFinite(num) || num === 0) return "";
  return str;
}

function buildInitialMaterialPrices(materials) {
  return materials.map((m) => ({
    hsn: m.hsn || m.hsnCode || "",
    basicPrice: toEditableNumberInput(m.basicPrice),
    gstPercent: toEditableNumberInput(
      m.gstPercent != null && m.gstPercent !== "" ? m.gstPercent : m.gst,
    ),
    comments:
      normalizeQuoteResponse(m.technicalResponse || m.comments || "") || "",
  }));
}

function buildInitialSupplierTerms(rfq) {
  if (Array.isArray(rfq?.terms) && rfq.terms.length > 0) {
    return rfq.terms.map((t) => ({
      sno: t.sno,
      rfqTerm: t.rfqTerm || "",
      termsResponse: t.termsResponse || "",
      comments: normalizeQuoteResponse(
        t.quoteResponse || t.comments || t.termsComments || "",
      ),
    }));
  }
  if (rfq?.rfqTerms) {
    return [
      {
        sno: 1,
        rfqTerm: rfq.rfqTerms,
        termsResponse: rfq.termsResponse || "",
        comments: normalizeQuoteResponse(rfq.quoteResponse || ""),
      },
    ];
  }
  return [];
}

function emptySubmitErrors() {
  return { formMessage: "", terms: {}, materials: {} };
}

function validateQuoteSubmission(materialPrices, supplierTerms, materials) {
  const errors = emptySubmitErrors();
  let hasErrors = false;

  if (materials.length === 0) {
    errors.formMessage = "No materials available to submit.";
    return { hasErrors: true, errors };
  }

  materialPrices.forEach((row, index) => {
    const fieldErrors = {};

    if (!String(row.hsn || "").trim()) {
      fieldErrors.hsn = "HSN is required.";
      hasErrors = true;
    }

    if (!String(row.basicPrice || "").trim()) {
      fieldErrors.basicPrice = "Price is required.";
      hasErrors = true;
    } else if (!Number.isFinite(parseDecimalValue(row.basicPrice))) {
      fieldErrors.basicPrice = "Enter a valid Basic Price.";
      hasErrors = true;
    }

    if (!String(row.gstPercent || "").trim()) {
      fieldErrors.gstPercent = "GST required.";
      hasErrors = true;
    } else if (!Number.isFinite(parseDecimalValue(row.gstPercent))) {
      fieldErrors.gstPercent = "Enter a valid GST %.";
      hasErrors = true;
    }

    if (!String(row.comments || "").trim()) {
      fieldErrors.comments = "Technical Response is required.";
      hasErrors = true;
    }

    if (Object.keys(fieldErrors).length > 0) {
      errors.materials[index] = fieldErrors;
    }
  });

  supplierTerms.forEach((row, index) => {
    if (!String(row.comments || "").trim()) {
      errors.terms[index] = "Quote Response is required.";
      hasErrors = true;
    }
  });

  if (hasErrors && !errors.formMessage) {
    errors.formMessage =
      "Please complete all mandatory fields before submitting.";
  }

  return { hasErrors, errors };
}

function mapAttachmentFromApi(item) {
  return {
    s3Key: item?.s3Key || "",
    originalFileName: item?.originalFileName || "",
    contentType: item?.contentType || "",
    uploadedAt: item?.uploadedAt || "",
    url: item?.url || "",
  };
}

function getAttachmentLabel(att) {
  if (!att) return "Attachment";
  if (att.originalFileName) return att.originalFileName;
  const key = att.s3Key || "";
  if (!key) return "Attachment";
  const parts = String(key).split("/");
  return parts[parts.length - 1] || key;
}

function getFileExtension(fileName, contentType) {
  const name = String(fileName || "").trim();
  const dotExt = name.includes(".") ? name.split(".").pop().toLowerCase() : "";
  if (dotExt && dotExt.length <= 6) return dotExt;

  const typeMap = {
    "application/pdf": "pdf",
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/gif": "gif",
    "image/webp": "webp",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      "xlsx",
    "application/vnd.ms-excel": "xls",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      "docx",
    "application/msword": "doc",
    "text/plain": "txt",
    "application/zip": "zip",
  };
  return typeMap[String(contentType || "").toLowerCase()] || "";
}

function isSupplierFileAllowed(file) {
  const ext = getFileExtension(file?.name, file?.type);
  return SUPPLIER_ALLOWED_EXTENSIONS.includes(ext);
}

function formatAllowedFormatsList() {
  return SUPPLIER_ALLOWED_EXTENSIONS.map((ext) => ext.toUpperCase()).join(", ");
}

function getFileTypeMeta(extension, contentType) {
  const ext = String(
    extension || getFileExtension("", contentType),
  ).toLowerCase();

  if (ext === "pdf") {
    return { icon: "fa-file-pdf", color: "#dc3545", bg: "#fde8ea" };
  }
  if (ext === "xlsx" || ext === "xls" || ext === "csv") {
    return { icon: "fa-file-excel", color: "#198754", bg: "#e8f5ee" };
  }
  if (ext === "docx" || ext === "doc") {
    return { icon: "fa-file-word", color: "#0d6efd", bg: "#e7f1ff" };
  }
  if (["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].includes(ext)) {
    return { icon: "fa-file-image", color: "#6f42c1", bg: "#f3ebff" };
  }
  if (ext === "zip" || ext === "rar" || ext === "7z") {
    return { icon: "fa-file-zipper", color: "#fd7e14", bg: "#fff3e6" };
  }
  return { icon: "fa-file", color: "#495057", bg: "#f8f9fa" };
}

const AttachmentTileGrid = ({
  attachments,
  downloadingS3Key,
  onDownload,
  disabled,
  onRemove,
}) => {
  if (!attachments?.length) return null;

  return (
    <div className="d-flex flex-wrap" style={{ gap: "16px" }}>
      {attachments.map((att) => {
        const fileName = getAttachmentLabel(att);
        const fileMeta = getFileTypeMeta(
          getFileExtension(fileName, att.contentType),
          att.contentType,
        );
        const isDownloading = downloadingS3Key === att.s3Key;
        return (
          <div
            key={att.s3Key || fileName}
            style={{ position: "relative", width: "92px" }}
          >
            <button
              type="button"
              className="border-0 text-center w-100"
              title={`Open ${fileName}`}
              onClick={() => onDownload(att.s3Key, att.url)}
              disabled={disabled || isDownloading}
              style={{
                padding: "10px 8px",
                borderRadius: "10px",
                backgroundColor: fileMeta.bg,
                border: `1px solid ${fileMeta.color}33`,
                cursor: disabled || isDownloading ? "not-allowed" : "pointer",
                opacity: isDownloading ? 0.7 : 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  color: fileMeta.color,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "20px",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                  marginBottom: "8px",
                }}
              >
                {isDownloading ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  />
                ) : (
                  <i className={`fa-solid ${fileMeta.icon}`} />
                )}
              </div>
              <div
                title={fileName}
                style={{
                  fontSize: "11px",
                  lineHeight: 1.3,
                  wordBreak: "break-word",
                  color: "#212529",
                  width: "100%",
                }}
              >
                {fileName.length > 22
                  ? `${fileName.slice(0, 19)}...`
                  : fileName}
              </div>
            </button>
            {onRemove ? (
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                title="Remove"
                onClick={() => onRemove(att.s3Key)}
                style={{
                  position: "absolute",
                  top: "-6px",
                  right: "-6px",
                  padding: "0 5px",
                  lineHeight: "18px",
                  borderRadius: "50%",
                }}
              >
                ×
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

const ViewField = ({ label, children }) => (
  <div className="form-group row align-items-center">
    <label className="col-12 col-md-4 col-form-label">{label}</label>
    <div className="col-12 col-md-8">
      <div className="txtdsply">{children}</div>
    </div>
  </div>
);

const SectionTitle = ({ icon, title }) => (
  <div className="row mb-2 mt-3">
    <div className="col-12">
      <h5 className="text-primary border-bottom pb-2 mb-3">
        <i className={`fa-solid ${icon} mr-2`} />
        {title}
      </h5>
    </div>
  </div>
);

function quoteStatusBadgeClass(status) {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();
  if (normalized === "submitted") return "badge-primary";
  if (normalized === "open" || normalized === "pending") return "badge-warning";
  if (normalized.includes("closed")) return "badge-secondary";
  return "badge-info";
}

const RfqSupplierView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const termsSectionRef = useRef(null);
  const supplierAttachmentInputRef = useRef(null);

  const [rfq, setRfq] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [downloadingS3Key, setDownloadingS3Key] = useState("");
  const [isPriceEditMode, setIsPriceEditMode] = useState(false);
  const [materialPrices, setMaterialPrices] = useState([]);
  const [supplierTerms, setSupplierTerms] = useState([]);
  const [submitErrors, setSubmitErrors] = useState(emptySubmitErrors());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingSupplierFiles, setPendingSupplierFiles] = useState([]);
  const [removedSupplierAttachmentKeys, setRemovedSupplierAttachmentKeys] =
    useState(() => new Set());
  const [isUploadingSupplierAttachments, setIsUploadingSupplierAttachments] =
    useState(false);
  const [showSubmitSuccess, setShowSubmitSuccess] = useState(false);

  const supplierId = localStorage.getItem("supplierId") || "";
  const currentUserId = localStorage.getItem("id") || "";

  const materials = useMemo(
    () => (Array.isArray(rfq?.materials) ? rfq.materials : []),
    [rfq],
  );

  const rfqOwnerAttachments = useMemo(() => {
    if (!Array.isArray(rfq?.rfqOwner)) return [];
    return rfq.rfqOwner.map(mapAttachmentFromApi).filter((att) => att.s3Key);
  }, [rfq?.rfqOwner]);

  const savedSupplierAttachments = useMemo(() => {
    if (!Array.isArray(rfq?.attachments)) return [];
    return rfq.attachments.map(mapAttachmentFromApi).filter((att) => att.s3Key);
  }, [rfq?.attachments]);

  const visibleSupplierAttachments = useMemo(
    () =>
      savedSupplierAttachments.filter(
        (att) => !removedSupplierAttachmentKeys.has(att.s3Key),
      ),
    [savedSupplierAttachments, removedSupplierAttachmentKeys],
  );

  const supplierAttachmentCount =
    visibleSupplierAttachments.length + pendingSupplierFiles.length;

  const quoteSubmitted = isQuoteSubmitted(rfq);
  const isQuotationClosed = isRfqDueDatePassed(rfq?.rfqDueDate);
  const canUpdateQuote = !isQuotationClosed;
  const showMaterialPricing =
    quoteSubmitted || (isPriceEditMode && canUpdateQuote);
  const showTermsComments =
    quoteSubmitted ||
    (isPriceEditMode && canUpdateQuote) ||
    supplierTerms.some((t) => String(t.comments || "").trim());

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const response = await axios.post(API_URL, {
        action: "get-supplier-rfq-detail",
        rfqid: id,
        currentUserId: currentUserId || undefined,
      });

      const responseData = normalizeApiPayload(parseApiResponse(response.data));

      if (isApiSuccess(responseData)) {
        const row = pickRfqFromResponse(responseData);
        if (row) {
          setRfq(row);
        } else {
          toast.error("RFQ not found", { autoClose: 2000 });
          navigate("/rfqSupplierList");
        }
      } else {
        toast.error(responseData?.message || "Failed to fetch RFQ details", {
          autoClose: 2000,
        });
        navigate("/rfqSupplierList");
      }
    } catch (error) {
      console.error("Fetch supplier RFQ detail error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        errData?.message || "An error occurred while fetching RFQ details",
        { autoClose: 2000 },
      );
      navigate("/rfqSupplierList");
    } finally {
      setIsLoading(false);
    }
  }, [id, currentUserId, navigate]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  useEffect(() => {
    setMaterialPrices(buildInitialMaterialPrices(materials));
  }, [materials]);

  useEffect(() => {
    setSupplierTerms(buildInitialSupplierTerms(rfq));
  }, [rfq]);

  useEffect(() => {
    if (isQuotationClosed && isPriceEditMode) {
      setIsPriceEditMode(false);
    }
  }, [isQuotationClosed, isPriceEditMode]);

  const pricedMaterials = useMemo(
    () =>
      materials.map((material, index) => {
        const priceRow = materialPrices[index] || {
          basicPrice: "",
          gstPercent: "",
          hsn: "",
          comments: "",
        };
        return {
          material,
          priceRow,
          ...calculateMaterialPricing(material, priceRow),
        };
      }),
    [materials, materialPrices],
  );

  const grossTotals = useMemo(() => {
    const calculated = pricedMaterials.reduce(
      (acc, row) => ({
        grossBasicPrice: acc.grossBasicPrice + row.lineBasic,
        grossGstValue: acc.grossGstValue + row.gstValue,
        grossTotalValue: acc.grossTotalValue + row.totalValue,
      }),
      { grossBasicPrice: 0, grossGstValue: 0, grossTotalValue: 0 },
    );

    if (!quoteSubmitted || isPriceEditMode) {
      return calculated;
    }

    const gross =
      rfq?.grossTotal && typeof rfq.grossTotal === "object"
        ? rfq.grossTotal
        : {};
    return {
      grossBasicPrice:
        Number(gross.grossTotalBasicPrice) || calculated.grossBasicPrice,
      grossGstValue: Number(gross.grossGstValue) || calculated.grossGstValue,
      grossTotalValue:
        Number(gross.grossTotalValue) || calculated.grossTotalValue,
    };
  }, [pricedMaterials, quoteSubmitted, isPriceEditMode, rfq]);

  const downloadRfqOwnerAttachment = async (s3Key, fallbackUrl) => {
    if (!s3Key || downloadingS3Key) return;
    setDownloadingS3Key(s3Key);
    try {
      const response = await axios.post(API_URL, {
        action: "get-rfq-owner-attachment-download-presigned-url",
        s3Keys: [s3Key],
        expiresIn: 3600,
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      if (!isApiSuccess(responseData)) {
        toast.error(responseData?.message || "Failed to get download URL", {
          autoClose: 2000,
        });
        return;
      }
      const downloadUrl =
        responseData.files?.[0]?.downloadUrl ||
        responseData.downloadUrl ||
        fallbackUrl;
      if (!downloadUrl) {
        toast.error("Download URL not available", { autoClose: 2000 });
        return;
      }
      window.open(downloadUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Download attachment error:", error);
      if (fallbackUrl) {
        window.open(fallbackUrl, "_blank", "noopener,noreferrer");
      } else {
        toast.error("Failed to download attachment", { autoClose: 2000 });
      }
    } finally {
      setDownloadingS3Key("");
    }
  };

  const downloadSupplierAttachment = async (s3Key, fallbackUrl) => {
    if (!s3Key || downloadingS3Key) return;
    if (!supplierId) {
      toast.error("Supplier ID is missing. Please login again.", {
        autoClose: 2500,
      });
      return;
    }
    setDownloadingS3Key(s3Key);
    try {
      const response = await axios.post(API_URL, {
        action: "get-rfq-supplier-attachment-download-presigned-url",
        rfqNumber: rfq?.rfqNumber || rfq?.rfqNo || "",
        rfqid: id,
        supplierId,
        s3Keys: [s3Key],
        expiresIn: 3600,
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      if (!isApiSuccess(responseData)) {
        toast.error(responseData?.message || "Failed to get download URL", {
          autoClose: 2000,
        });
        return;
      }
      const downloadUrl =
        responseData.files?.[0]?.downloadUrl ||
        responseData.downloadUrl ||
        fallbackUrl;
      if (!downloadUrl) {
        toast.error("Download URL not available", { autoClose: 2000 });
        return;
      }
      window.open(downloadUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Download supplier attachment error:", error);
      if (fallbackUrl) {
        window.open(fallbackUrl, "_blank", "noopener,noreferrer");
      } else {
        toast.error("Failed to download attachment", { autoClose: 2000 });
      }
    } finally {
      setDownloadingS3Key("");
    }
  };

  const handleSupplierAttachmentFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;

    const invalidFiles = files.filter((file) => !isSupplierFileAllowed(file));
    if (invalidFiles.length > 0) {
      toast.error(
        `Invalid file type. Allowed formats: ${formatAllowedFormatsList()}`,
        { autoClose: 3000 },
      );
      return;
    }

    if (supplierAttachmentCount + files.length > MAX_SUPPLIER_ATTACHMENTS) {
      toast.error(`Maximum ${MAX_SUPPLIER_ATTACHMENTS} attachments allowed`, {
        autoClose: 2000,
      });
      return;
    }

    setPendingSupplierFiles((prev) => [...prev, ...files]);
  };

  const removePendingSupplierFile = (index) => {
    setPendingSupplierFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const markSupplierAttachmentForRemoval = (s3Key) => {
    setRemovedSupplierAttachmentKeys((prev) => {
      const next = new Set(prev);
      next.add(s3Key);
      return next;
    });
  };

  const uploadPendingSupplierAttachments = async () => {
    if (pendingSupplierFiles.length === 0) return [];

    setIsUploadingSupplierAttachments(true);
    try {
      const files = pendingSupplierFiles;
      const contentTypesForPresign = files.map((f) => resolveFileContentType(f));

      const response = await axios.post(API_URL, {
        action: "get-rfq-supplier-attachment-upload-presigned-url",
        rfqid: id,
        supplierId,
        fileNames: files.map((f) => f.name),
        contentTypes: contentTypesForPresign,
        expiresIn: 7200,
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));

      if (!isApiSuccess(responseData) || !Array.isArray(responseData.files)) {
        const invalidMsg = Array.isArray(responseData?.invalidFiles)
          ? `${responseData.message || "Invalid file type"}`
          : responseData?.message || "Failed to get upload URLs";
        toast.error(invalidMsg, { autoClose: 3000 });
        throw new Error(invalidMsg);
      }

      const presignedFiles = responseData.files;
      if (presignedFiles.length !== files.length) {
        throw new Error("Upload URL count mismatch");
      }

      for (let i = 0; i < files.length; i++) {
        const meta = presignedFiles[i];
        const file = files[i];
        const contentType =
          meta.contentType ||
          contentTypesForPresign[i] ||
          "application/octet-stream";

        if (!meta.uploadUrl) {
          throw new Error(`Missing uploadUrl for ${file.name}`);
        }
        if (!(file instanceof Blob) || file.size <= 0) {
          throw new Error(`Invalid file for upload: ${file.name}`);
        }

        const putRes = await fetch(meta.uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": contentType },
          body: file,
        });

        if (!putRes.ok) {
          throw new Error(`S3 upload failed for ${file.name}: ${putRes.status}`);
        }
      }

      return presignedFiles.map((meta, i) => ({
        s3Key: meta.s3Key,
        originalFileName:
          meta.originalFileName || meta.fileName || files[i].name,
        contentType:
          meta.contentType ||
          contentTypesForPresign[i] ||
          "application/octet-stream",
      }));
    } finally {
      setIsUploadingSupplierAttachments(false);
    }
  };

  const clearMaterialFieldError = (index, field) => {
    setSubmitErrors((prev) => {
      const rowErrors = prev.materials[index];
      if (!rowErrors?.[field]) return prev;

      const nextRowErrors = { ...rowErrors };
      delete nextRowErrors[field];

      const nextMaterials = { ...prev.materials };
      if (Object.keys(nextRowErrors).length === 0) {
        delete nextMaterials[index];
      } else {
        nextMaterials[index] = nextRowErrors;
      }

      return { ...prev, materials: nextMaterials, formMessage: "" };
    });
  };

  const handleMaterialPriceChange = (index, field, value) => {
    let nextValue = value;
    if (field !== "comments" && field !== "hsn") {
      if (!isDecimalInput(value)) return;
      nextValue = normalizeDecimalInput(value);
      if (!isDecimalInput(nextValue)) return;
    }
    clearMaterialFieldError(index, field);
    setMaterialPrices((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: nextValue };
      return next;
    });
  };

  const handleTermsCommentChange = (index, value) => {
    setSubmitErrors((prev) => {
      if (!prev.terms[index]) return prev;
      const nextTerms = { ...prev.terms };
      delete nextTerms[index];
      return { ...prev, terms: nextTerms, formMessage: "" };
    });
    setSupplierTerms((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], comments: value };
      return next;
    });
  };

  const handleStartPriceUpdate = () => {
    if (isRfqDueDatePassed(rfq?.rfqDueDate)) {
      setSubmitErrors({
        ...emptySubmitErrors(),
        formMessage:
          "RFQ due date has passed. Quotation can no longer be updated.",
      });
      return;
    }
    setSubmitErrors(emptySubmitErrors());
    setPendingSupplierFiles([]);
    setRemovedSupplierAttachmentKeys(new Set());
    setShowSubmitSuccess(false);
    setIsPriceEditMode(true);
    requestAnimationFrame(() => {
      termsSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const handleCancelPriceUpdate = () => {
    setMaterialPrices(buildInitialMaterialPrices(materials));
    setSupplierTerms(buildInitialSupplierTerms(rfq));
    setSubmitErrors(emptySubmitErrors());
    setPendingSupplierFiles([]);
    setRemovedSupplierAttachmentKeys(new Set());
    setIsPriceEditMode(false);
  };

  const scrollToFirstInvalidField = () => {
    requestAnimationFrame(() => {
      const firstInvalid = document.querySelector(".is-invalid");
      if (firstInvalid) {
        firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
        if (typeof firstInvalid.focus === "function") {
          firstInvalid.focus({ preventScroll: true });
        }
      }
    });
  };

  const handleSubmitPrices = async () => {
    if (isRfqDueDatePassed(rfq?.rfqDueDate)) {
      setSubmitErrors({
        ...emptySubmitErrors(),
        formMessage:
          "RFQ due date has passed. Quotation can no longer be submitted.",
      });
      return;
    }

    if (!supplierId) {
      setSubmitErrors({
        ...emptySubmitErrors(),
        formMessage: "Supplier ID is missing. Please login again.",
      });
      return;
    }

    const { hasErrors, errors } = validateQuoteSubmission(
      materialPrices,
      supplierTerms,
      materials,
    );
    if (hasErrors) {
      setSubmitErrors(errors);
      scrollToFirstInvalidField();
      return;
    }

    setSubmitErrors(emptySubmitErrors());
    setIsSubmitting(true);
    try {
      let newlyUploadedAttachments = [];
      if (pendingSupplierFiles.length > 0) {
        newlyUploadedAttachments = await uploadPendingSupplierAttachments();
      }

      const finalAttachments = [
        ...visibleSupplierAttachments,
        ...newlyUploadedAttachments,
      ];

      const payload = {
        action: "update-rfq-supplier-quote",
        rfqid: id,
        supplierId,
        currentUserId: currentUserId || undefined,
        terms: supplierTerms.map((row) => ({
          rfqTerm: String(row.rfqTerm || "").trim(),
          termsResponse: String(row.termsResponse || "").trim(),
          comments: String(row.comments || "").trim(),
        })),
        materialQuotes: pricedMaterials.map((row) => {
          const quoteRow = {
            materialCode: row.material.materialCode || "",
            materialDescription: row.material.materialDescription || "",
            hsn: String(row.priceRow.hsn || "").trim(),
            basicPrice: row.lineBasic,
            gstPercent: parseDecimalValue(row.priceRow.gstPercent) || 0,
            gstValue: row.gstValue,
            totalValue: row.totalValue,
            comments: (row.priceRow.comments || "").trim(),
          };
          const prId = getMaterialPrId(row.material);
          if (prId) {
            quoteRow.pr_id = prId;
          }
          return quoteRow;
        }),
        grossTotalBasicPrice: grossTotals.grossBasicPrice,
        grossGstValue: grossTotals.grossGstValue,
        grossTotalValue: grossTotals.grossTotalValue,
      };

      if (finalAttachments.length > 0) {
        payload.attachments = finalAttachments.map((att) => ({
          s3Key: att.s3Key,
          originalFileName: att.originalFileName,
          contentType: att.contentType,
        }));
      } else if (
        savedSupplierAttachments.length > 0 ||
        removedSupplierAttachmentKeys.size > 0
      ) {
        payload.attachments = [];
      }

      const response = await axios.post(API_URL, payload);
      const responseData = normalizeApiPayload(parseApiResponse(response.data));

      if (isApiSuccess(responseData)) {
        toast.success(
          responseData.message ||
            "Thank you for your response. Your quotation has been submitted successfully.",
          { autoClose: 3000 },
        );
        setShowSubmitSuccess(true);
        setIsPriceEditMode(false);
        setPendingSupplierFiles([]);
        setRemovedSupplierAttachmentKeys(new Set());
        await fetchDetail();
      } else {
        toast.error(responseData?.message || "Failed to submit quote", {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Submit RFQ quote error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        errData?.message ||
          error.message ||
          "An error occurred while submitting quote",
        { autoClose: 3000 },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const header = (
    <div className="content-header">
      <div className="container-fluid">
        <div className="row mb-2 align-items-center">
          <div className="col-6 col-sm-6">
            <h1 className="m-0 txt_lft">View RFQ</h1>
          </div>
          <div className="col-6 col-sm-6">
            <ol className="breadcrumb justify-content-end mb-0">
              <li className="breadcrumb-item">
                <Link to="/rfqSupplierList">
                  <i className="fa-solid fa-arrow-left-long" /> Back
                </Link>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div>
        {header}
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!rfq) {
    return (
      <div>
        {header}
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <p>RFQ not found</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const rfqNo = rfq.rfqNumber || rfq.rfqNo || "-";
  const currency = rfq.rfqCurrency || "";

  return (
    <div>
      {header}
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <div className="card-body">
                    <SectionTitle icon="fa-file-lines" title="RFQ Details" />

                    <ViewField label="RFQ Number">{rfqNo}</ViewField>
                    <ViewField label="RFQ Date">
                      {formatDate(rfq.rfqDate)}
                    </ViewField>
                    <ViewField label="RFQ Due Date">
                      {formatDueDateTime(rfq.rfqDueDate)}
                    </ViewField>
                    <ViewField label="Currency">
                      {rfq.rfqCurrency || "-"}
                    </ViewField>
                    <ViewField label="Quote Status">
                      <span
                        className={`badge ${quoteStatusBadgeClass(
                          rfq.quoteStatus,
                        )}`}
                        style={{ fontSize: "90%", fontWeight: 550 }}
                      >
                        {capitalizeStatus(rfq.quoteStatus)}
                      </span>
                    </ViewField>
                    <ViewField label="Quote Submitted">
                      <span
                        className={`badge ${
                          quoteSubmitted ? "badge-success" : "badge-secondary"
                        }`}
                        style={{ fontSize: "90%", fontWeight: 550 }}
                      >
                        {quoteSubmitted ? "Yes" : "No"}
                      </span>
                    </ViewField>
                    {quoteSubmitted ? (
                      <ViewField label="Submitted At">
                        {formatDateTime(rfq.quoteSubmittedAt)}
                      </ViewField>
                    ) : null}
                    <ViewField label="RFQ Summary">
                      <span style={{ whiteSpace: "pre-wrap" }}>
                        {rfq.rfqSummary || "-"}
                      </span>
                    </ViewField>

                    {(rfq.pdfLink || rfqOwnerAttachments.length > 0) && (
                      <>
                        <SectionTitle icon="fa-file-pdf" title="Document" />
                        {rfq.pdfLink ? (
                          <ViewField label="PDF Link">
                            <a
                              href={rfq.pdfLink}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View PDF
                            </a>
                          </ViewField>
                        ) : null}
                        {rfqOwnerAttachments.length > 0 ? (
                          <ViewField label="RFQ Owner Attachments">
                            <AttachmentTileGrid
                              attachments={rfqOwnerAttachments}
                              downloadingS3Key={downloadingS3Key}
                              onDownload={downloadRfqOwnerAttachment}
                              disabled={Boolean(downloadingS3Key)}
                            />
                          </ViewField>
                        ) : null}
                      </>
                    )}

                    <div ref={termsSectionRef}>
                      <SectionTitle icon="fa-list-check" title="Terms" />
                    </div>

                    {supplierTerms.length === 0 ? (
                      <div className="txtdsply text-muted mb-3">No terms</div>
                    ) : (
                      <div className="table-responsive mb-3">
                        <table className="table table-bordered table-sm mb-0">
                          <thead>
                            <tr
                              style={{
                                backgroundColor: "#003366",
                                color: "#fff",
                              }}
                            >
                              <th style={{ fontSize: "12px", width: "48px" }}>
                                S.No
                              </th>
                              <th
                                style={{ fontSize: "12px", minWidth: "160px" }}
                              >
                                RFQ Term
                              </th>
                              <th
                                style={{ fontSize: "12px", minWidth: "200px" }}
                              >
                                Terms Response
                              </th>
                              {(showTermsComments || isPriceEditMode) && (
                                <th
                                  style={{
                                    fontSize: "12px",
                                    minWidth: "200px",
                                  }}
                                >
                                  Quote Response
                                  {isPriceEditMode ? (
                                    <span className="text-warning"> *</span>
                                  ) : null}
                                </th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {supplierTerms.map((row, index) => (
                              <tr key={index}>
                                <td style={{ verticalAlign: "top" }}>
                                  {row.sno ?? index + 1}
                                </td>
                                <td
                                  style={{
                                    verticalAlign: "top",
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {row.rfqTerm || "-"}
                                </td>
                                <td
                                  style={{
                                    verticalAlign: "top",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {row.termsResponse || "-"}
                                </td>
                                {(showTermsComments || isPriceEditMode) && (
                                  <td style={{ verticalAlign: "top" }}>
                                    {isPriceEditMode ? (
                                      <>
                                        <textarea
                                          className={`form-control form-control-sm${
                                            submitErrors.terms[index]
                                              ? " is-invalid"
                                              : ""
                                          }`}
                                          rows={2}
                                          placeholder="Enter comments..."
                                          value={row.comments}
                                          onChange={(e) =>
                                            handleTermsCommentChange(
                                              index,
                                              e.target.value,
                                            )
                                          }
                                        />
                                        {submitErrors.terms[index] ? (
                                          <div className="invalid-feedback d-block">
                                            {submitErrors.terms[index]}
                                          </div>
                                        ) : null}
                                      </>
                                    ) : (
                                      <span
                                        style={{
                                          whiteSpace: "pre-wrap",
                                          wordBreak: "break-word",
                                          display: "block",
                                        }}
                                      >
                                        {row.comments || "-"}
                                      </span>
                                    )}
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <SectionTitle icon="fa-boxes-stacked" title="Materials" />

                    {materials.length === 0 ? (
                      <div className="txtdsply text-muted mb-3">
                        No materials
                      </div>
                    ) : (
                      <div className="table-responsive mb-3">
                        <table
                          className="table table-bordered table-sm mb-0"
                          style={{
                            minWidth: showMaterialPricing ? "1180px" : "720px",
                          }}
                        >
                          <thead>
                            <tr
                              style={{
                                backgroundColor: "#003366",
                                color: "#fff",
                              }}
                            >
                              <th style={{ fontSize: "12px" }}>S.No</th>
                              <th style={{ fontSize: "12px" }}>
                                Material Code
                              </th>
                              <th style={{ fontSize: "12px" }}>
                                Material Description
                              </th>
                              <th
                                style={{
                                  fontSize: "12px",
                                  width: "85px",
                                  minWidth: "85px",
                                  maxWidth: "85px",
                                }}
                              >
                                HSN
                                {isPriceEditMode ? (
                                  <span className="text-warning"> *</span>
                                ) : null}
                              </th>
                              <th style={{ fontSize: "12px" }}>UOM</th>
                              <th style={{ fontSize: "12px" }}>Quantity</th>
                              {showMaterialPricing ? (
                                <>
                                  <th
                                    style={{
                                      fontSize: "12px",
                                      width: "88px",
                                      minWidth: "88px",
                                      maxWidth: "96px",
                                    }}
                                  >
                                    Basic Price
                                    {isPriceEditMode ? (
                                      <span className="text-warning"> *</span>
                                    ) : null}
                                  </th>
                                  <th
                                    style={{
                                      fontSize: "12px",
                                      width: "78px",
                                      minWidth: "78px",
                                      maxWidth: "86px",
                                    }}
                                  >
                                    GST %
                                    {isPriceEditMode ? (
                                      <span className="text-warning"> *</span>
                                    ) : null}
                                  </th>
                                  <th style={{ fontSize: "12px" }}>
                                    GST Value
                                  </th>
                                  <th style={{ fontSize: "12px" }}>
                                    Total Value
                                  </th>
                                  <th
                                    style={{
                                      fontSize: "12px",
                                      minWidth: "140px",
                                    }}
                                  >
                                    Technical Response
                                    {isPriceEditMode ? (
                                      <span className="text-warning"> *</span>
                                    ) : null}
                                  </th>
                                </>
                              ) : null}
                            </tr>
                          </thead>
                          <tbody>
                            {pricedMaterials.map((row, index) => (
                              <tr
                                key={
                                  getMaterialPrId(row.material) ||
                                  `${row.material.materialCode || "m"}-${index}`
                                }
                              >
                                <td>{row.material.sno ?? index + 1}</td>
                                <td>{row.material.materialCode || "-"}</td>
                                <td
                                  style={{
                                    maxWidth: "240px",
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {row.material.materialDescription || "-"}
                                </td>
                                <td
                                  style={{
                                    width: "72px",
                                    minWidth: "95px",
                                    maxWidth: "95px",
                                  }}
                                >
                                  {isPriceEditMode ? (
                                    <>
                                      <input
                                        type="text"
                                        className={`form-control form-control-sm${
                                          submitErrors.materials[index]?.hsn
                                            ? " is-invalid"
                                            : ""
                                        }`}
                                        placeholder="HSN"
                                        value={row.priceRow.hsn || ""}
                                        onChange={(e) =>
                                          handleMaterialPriceChange(
                                            index,
                                            "hsn",
                                            e.target.value,
                                          )
                                        }
                                        style={{
                                          width: "100%",
                                          maxWidth: "85px",
                                          paddingLeft: "2px",
                                          paddingRight: "2px",
                                        }}
                                      />
                                      {submitErrors.materials[index]?.hsn ? (
                                        <div className="invalid-feedback d-block">
                                          {submitErrors.materials[index].hsn}
                                        </div>
                                      ) : null}
                                    </>
                                  ) : (
                                    row.priceRow.hsn ||
                                    row.material.hsn ||
                                    row.material.hsnCode ||
                                    "-"
                                  )}
                                </td>
                                <td>{row.material.uom || "-"}</td>
                                <td>{row.material.quantity ?? "-"}</td>
                                {showMaterialPricing ? (
                                  <>
                                    <td
                                      className={
                                        isPriceEditMode
                                          ? undefined
                                          : "text-right align-middle"
                                      }
                                      style={{
                                        width: "88px",
                                        minWidth: "88px",
                                        maxWidth: "96px",
                                      }}
                                    >
                                      {isPriceEditMode ? (
                                        <>
                                          <input
                                            type="text"
                                            className={`form-control form-control-sm${
                                              submitErrors.materials[index]
                                                ?.basicPrice
                                                ? " is-invalid"
                                                : ""
                                            }`}
                                            inputMode="decimal"
                                            placeholder=""
                                            value={row.priceRow.basicPrice}
                                            onChange={(e) =>
                                              handleMaterialPriceChange(
                                                index,
                                                "basicPrice",
                                                e.target.value,
                                              )
                                            }
                                            style={{
                                              width: "100%",
                                              maxWidth: "88px",
                                              paddingLeft: "6px",
                                              paddingRight: "6px",
                                            }}
                                          />
                                          {submitErrors.materials[index]
                                            ?.basicPrice ? (
                                            <div className="invalid-feedback d-block">
                                              {
                                                submitErrors.materials[index]
                                                  .basicPrice
                                              }
                                            </div>
                                          ) : null}
                                        </>
                                      ) : (
                                        formatAmount(
                                          row.material.basicPrice ??
                                            row.priceRow.basicPrice,
                                        )
                                      )}
                                    </td>
                                    <td
                                      className={
                                        isPriceEditMode
                                          ? undefined
                                          : "text-right align-middle"
                                      }
                                      style={{
                                        width: "78px",
                                        minWidth: "78px",
                                        maxWidth: "86px",
                                      }}
                                    >
                                      {isPriceEditMode ? (
                                        <>
                                          <input
                                            type="text"
                                            className={`form-control form-control-sm${
                                              submitErrors.materials[index]
                                                ?.gstPercent
                                                ? " is-invalid"
                                                : ""
                                            }`}
                                            inputMode="decimal"
                                            placeholder=""
                                            value={row.priceRow.gstPercent}
                                            onChange={(e) =>
                                              handleMaterialPriceChange(
                                                index,
                                                "gstPercent",
                                                e.target.value,
                                              )
                                            }
                                            style={{
                                              width: "100%",
                                              maxWidth: "78px",
                                              paddingLeft: "6px",
                                              paddingRight: "6px",
                                            }}
                                          />
                                          {submitErrors.materials[index]
                                            ?.gstPercent ? (
                                            <div className="invalid-feedback d-block">
                                              {
                                                submitErrors.materials[index]
                                                  .gstPercent
                                              }
                                            </div>
                                          ) : null}
                                        </>
                                      ) : (
                                        formatAmount(
                                          row.material.gstPercent ??
                                            row.priceRow.gstPercent,
                                        )
                                      )}
                                    </td>
                                    <td className="text-right align-middle">
                                      {formatAmount(
                                        isPriceEditMode
                                          ? row.gstValue
                                          : row.material.gstValue ??
                                              row.gstValue,
                                      )}
                                    </td>
                                    <td className="text-right fwt_600 align-middle">
                                      {formatAmount(
                                        isPriceEditMode
                                          ? row.totalValue
                                          : row.material.totalValue ??
                                              row.totalValue,
                                      )}
                                    </td>
                                    <td>
                                      {isPriceEditMode ? (
                                        <>
                                          <textarea
                                            className={`form-control form-control-sm${
                                              submitErrors.materials[index]
                                                ?.comments
                                                ? " is-invalid"
                                                : ""
                                            }`}
                                            rows={2}
                                            placeholder="Enter comments..."
                                            value={row.priceRow.comments}
                                            onChange={(e) =>
                                              handleMaterialPriceChange(
                                                index,
                                                "comments",
                                                e.target.value,
                                              )
                                            }
                                          />
                                          {submitErrors.materials[index]
                                            ?.comments ? (
                                            <div className="invalid-feedback d-block">
                                              {
                                                submitErrors.materials[index]
                                                  .comments
                                              }
                                            </div>
                                          ) : null}
                                        </>
                                      ) : (
                                        <span
                                          style={{
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-word",
                                            display: "block",
                                          }}
                                        >
                                          {normalizeQuoteResponse(
                                            row.material.technicalResponse ||
                                              row.material.comments ||
                                              row.priceRow.comments,
                                          ) || "-"}
                                        </span>
                                      )}
                                    </td>
                                  </>
                                ) : null}
                              </tr>
                            ))}
                            {showMaterialPricing ? (
                              <tr style={{ backgroundColor: "#f4f6f9" }}>
                                <td
                                  colSpan={6}
                                  className="text-right fwt_600 align-middle"
                                >
                                  Gross Total
                                  {currency ? ` (${currency})` : ""}
                                </td>
                                <td className="text-right fwt_600 align-middle">
                                  {formatAmount(grossTotals.grossBasicPrice)}
                                </td>
                                <td />
                                <td className="text-right fwt_600 align-middle">
                                  {formatAmount(grossTotals.grossGstValue)}
                                </td>
                                <td className="text-right fwt_600 align-middle">
                                  {formatAmount(grossTotals.grossTotalValue)}
                                </td>
                                <td />
                              </tr>
                            ) : null}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {(isPriceEditMode ||
                      visibleSupplierAttachments.length > 0) && (
                      <>
                        <SectionTitle
                          icon="fa-paperclip"
                          title="Your Quotation Attachments"
                        />
                        {isPriceEditMode ? (
                          <div className="mb-3">
                            <p
                              className="text-muted mb-2"
                              style={{ fontSize: "13px" }}
                            >
                              Upload your quotation reference documents (max{" "}
                              {MAX_SUPPLIER_ATTACHMENTS} files).
                            </p>
                            <input
                              ref={supplierAttachmentInputRef}
                              type="file"
                              multiple
                              accept=".jpeg,.jpg,.png,.pdf"
                              style={{ display: "none" }}
                              onChange={handleSupplierAttachmentFileSelect}
                              disabled={
                                isSubmitting || isUploadingSupplierAttachments
                              }
                            />
                            <button
                              type="button"
                              className="btn btn-blue btn-sm mb-3"
                              onClick={() =>
                                supplierAttachmentInputRef.current?.click()
                              }
                              disabled={
                                isSubmitting ||
                                isUploadingSupplierAttachments ||
                                supplierAttachmentCount >=
                                  MAX_SUPPLIER_ATTACHMENTS
                              }
                            >
                              <i className="fa-solid fa-paperclip mr-1" />
                              Choose Files
                            </button>

                            {visibleSupplierAttachments.length > 0 ? (
                              <div className="mb-3">
                                <AttachmentTileGrid
                                  attachments={visibleSupplierAttachments}
                                  downloadingS3Key={downloadingS3Key}
                                  onDownload={downloadSupplierAttachment}
                                  disabled={Boolean(downloadingS3Key)}
                                  onRemove={markSupplierAttachmentForRemoval}
                                />
                              </div>
                            ) : null}

                            {pendingSupplierFiles.length > 0 ? (
                              <ul className="list-group mb-3">
                                {pendingSupplierFiles.map((file, index) => (
                                  <li
                                    key={`${file.name}-${index}`}
                                    className="list-group-item d-flex justify-content-between align-items-center"
                                  >
                                    <span>
                                      <i className="fa-solid fa-file mr-2" />
                                      {file.name}
                                      <small className="text-muted ml-2">
                                        Ready to upload on submit
                                      </small>
                                    </span>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() =>
                                        removePendingSupplierFile(index)
                                      }
                                    >
                                      Remove
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                          </div>
                        ) : (
                          <AttachmentTileGrid
                            attachments={visibleSupplierAttachments}
                            downloadingS3Key={downloadingS3Key}
                            onDownload={downloadSupplierAttachment}
                            disabled={Boolean(downloadingS3Key)}
                          />
                        )}
                      </>
                    )}

                    {(quoteSubmitted || showSubmitSuccess) &&
                    !isPriceEditMode ? (
                      <div
                        className="alert alert-success mt-4 mb-0 text-center"
                        role="alert"
                      >
                        <i className="fa-solid fa-circle-check mr-2" />
                        Thank you for your response. Your quotation has been
                        submitted successfully.
                      </div>
                    ) : null}

                    {materials.length > 0 && canUpdateQuote ? (
                      <div className="mt-4 pt-3 border-top">
                        {submitErrors.formMessage ? (
                          <div className="alert alert-danger mb-3" role="alert">
                            {submitErrors.formMessage}
                          </div>
                        ) : null}
                        <div className="d-flex justify-content-end flex-wrap">
                          {!isPriceEditMode ? (
                            <button
                              type="button"
                              className="btn btn-blue"
                              onClick={handleStartPriceUpdate}
                            >
                              <i className="fa-solid fa-pen-to-square mr-1" />
                              Update Price
                            </button>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="btn btn-outline-secondary mr-2"
                                onClick={handleCancelPriceUpdate}
                                disabled={
                                  isSubmitting || isUploadingSupplierAttachments
                                }
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                className="btn btn-blue"
                                onClick={handleSubmitPrices}
                                disabled={
                                  isSubmitting || isUploadingSupplierAttachments
                                }
                              >
                                {isSubmitting ||
                                isUploadingSupplierAttachments ? (
                                  <>
                                    <span
                                      className="spinner-border spinner-border-sm mr-1"
                                      role="status"
                                      aria-hidden="true"
                                    />
                                    Submitting...
                                  </>
                                ) : (
                                  <>
                                    <i className="fa-solid fa-paper-plane mr-1" />
                                    Submit
                                  </>
                                )}
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ) : isQuotationClosed ? (
                      <div
                        className="alert mt-4 mb-0"
                        role="alert"
                        style={{
                          color: "#856404",
                          backgroundColor: "#fff3cd",
                          borderColor: "#ffeeba",
                        }}
                      >
                        <i className="fa-solid fa-triangle-exclamation mr-2" />
                        RFQ due date has passed. Quotation can no longer be
                        updated.
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RfqSupplierView;
